import S, { DataSignal } from 's-js';
import * as Promise from 'bluebird';
import TZDate from 'lib/TZDate';
import { AutoSaver } from 'lib/autoSave';
import { DraftSignal } from 'lib/draftSignals';
import { Error as ValidationError } from 'lib/validationWatcher';

// TH 07/10/2017 -- Bugfix.
//
// Ever since switching from using Kendo's combined datetimepicker to using a
// datepicker and a timepicker side-by-side, there have been a few lurking problems around
// auto-save. The issue is that what feels to the user like a single edit (set the date and
// time) now triggers two auto-save requests. That has downstream effects:
//   - ItineraryChangeService -- The server-side ItineraryChangeService can block some
//     attempted itinerary changes based on the rest of the PortCall itinerary. The
//     semantics are such that editing the date and later editing the time can produce
//     different results than editing the date and time simultaneously.
//   - Log entries -- For times that collect an audit trail in the PC or PJO Log, edits
//     will produce two log entries instead of one.
//
// For now, our fix is to wrap certain datetime edits in an extra popup -- edits won't
// get persisted to the underlying data model until the popup gets dismissed, at which
// point we'll persist both the date and time portions of the edit simultaneously.
//
// This does a pretty good job of handling the Log issue. But for the ItineraryChangeService
// issue it's really only a workaround. It'll still be the case that editing the date portion
// of a datetimepicker, dismissing the popup, then editing the time portion can have
// unexpected effects. But it should at least make that scenario less common.
//
// Note that we don't need to wrap every single DateTimePicker this way, just DateTimePickers
// used to edit an auto-saving field that participates in the ItineraryChangeService or
// generates Log entries.

export const enum DateTimeEditPopupDismissStatus {
    /** User canceled */
    Canceled,
    /** User hit OK without editing the time */
    OKNotEdited,
    /** User hit OK after editing the time */
    OKWasEdited
}

interface PopupParams<TValue> {
    value : DataSignal<TValue>;

    /**
     * Called when popup is dismissed.
     * @param status Flag indicating which user action triggered dismissal
     */
    onDismiss : (status : DateTimeEditPopupDismissStatus) => void;

    validation? : PopupValidationParams<TValue>;
}

interface PopupValidationParams<TValue> {
    /**
     * Fire a request to validate the draft value.
     * The returned Promise should complete after validation is complete.
     * The success/failure of the returned Promise is irrelevant.
     * To report success/failure of validation, use the validation.errors signal.
     */
    validate? : (val : TValue) => Promise<any>;

    /**
     * Should validation be performed on the initial value?
     * Defaults to false, which means the initial value will be assumed to be valid.
     */
    runOnStart? : boolean;

    /**
     * A signal containing a list of possible validation errors. Undefined errors will be ignored.
     */
    errors? : () => ValidationError[];
}

export type DateTimeEditPopupParams = PopupParams<TZDate>;
export type NullableDateTimeEditPopupParams = PopupParams<TZDate | null>;

function DateTimeEditPopupGeneric<TValue extends TZDate | null>({
    value,
    onDismiss,
    validation
} : PopupParams<TValue>) {
    const
        draft = DraftSignal(value, TZDate.nullableEq),
        validator = DateTimeValidator({ draft, validation });

    let saveQueued = false;

    return {
        Draft: (val? : TValue) => val === undefined ? draft() : draft(val),
        Save,
        Cancel: () => onDismiss(DateTimeEditPopupDismissStatus.Canceled),
        ValidationErrors: validator.Errors
    };

    function Save() {
        if (saveQueued) return;
        validator.QueuePostValidationTask(() => {
            saveQueued = false;

            // Don't save if there are errors
            if (validator.Errors().length) return;

            S.freeze(() => {
                const wasEdited = S.sample(draft.isCaching);
                draft.checkpoint().commit();
                onDismiss(wasEdited ? DateTimeEditPopupDismissStatus.OKWasEdited : DateTimeEditPopupDismissStatus.OKNotEdited);
            });
        });
    }
}

function DateTimeValidator<TValue extends TZDate | null>({ draft, validation = {} } : {
    draft : DraftSignal<TValue>,
    validation? : PopupValidationParams<TValue>
}) {
    const
        validate = validation.validate || (() => Promise.resolve()),
        writtenVersion = S.on(draft.onWrite, prev => prev + 1, 0, true);

    // If runOnStart, we initialize the validatedVersion to be less than the written version,
    // otherwise we assume initial state has been validated.
    let validatedVersion = S.sample(writtenVersion) + (validation.runOnStart ? -1 : 0),
        validationTask : Promise<void> | null = null,
        postValidationTasks : (() => void)[] = [];

    AutoSaver(draft.onWrite, () => {
        const version = writtenVersion();
        validationTask = validate(draft()).finally(() => {
            validatedVersion = version;
            validationTask = null;
        });
        postValidationTasks.forEach(task => validationTask!.finally(task));
        postValidationTasks = [];
        return validationTask;
    }, 0, !validation.runOnStart);

    return {
        QueuePostValidationTask,
        Errors: () => (validation.errors ? validation.errors() : [])
    };

    /**
     * Queue a task to run after the next pending validation, if any.
     * If no pending validation, queue the task to run on the next tick.
     */
    function QueuePostValidationTask(task : () => void) {
        if (S.sample(writtenVersion) === validatedVersion) Promise.resolve().then(task);
        else if (validationTask) validationTask.finally(task);
        else postValidationTasks.push(task);
    }
}

export function DateTimeEditPopup(params : DateTimeEditPopupParams) {
    return DateTimeEditPopupGeneric(params);
}
export type DateTimeEditPopup = ReturnType<typeof DateTimeEditPopup>;

export function NullableDateTimeEditPopup(params : NullableDateTimeEditPopupParams) {
    return DateTimeEditPopupGeneric(params);
}
export type NullableDateTimeEditPopup = ReturnType<typeof NullableDateTimeEditPopup>;



// WEBPACK FOOTER //
// ./apps/portcall/datetimeeditpopup/model.ts